import { getDecodedShopifyProductIdString } from "@utils/index"
import _ from "lodash-es"
import React from "react"

export const INTERNAL_USER_DATA_PUSHED_FOR_ELEVAR =
  "internal_user_data_pushed_for_elevar"
export const ELEVAR_USER_DATA = "dl_user_data"
const ELEVAR_SIGN_UP = "dl_sign_up"
const ELEVAR_LOGIN = "dl_login"
const ELEVAR_VIEW_ITEM_LIST = "dl_view_item_list"
const ELEVAR_VIEW_SEARCH_RESULTS = "dl_view_search_results"
const ELEVAR_SELECT_ITEM = "dl_select_item"
export const ELEVAR_VIEW_ITEM = "dl_view_item"
const ELEVAR_ADD_TO_CART = "dl_add_to_cart"
const ELEVAR_REMOVE_FROM_CART = "dl_remove_from_cart"
const ELEVAR_VIEW_CART = "dl_view_cart"

export interface ElevarProductProps {
  brand: string
  compareAtPrice?: string
  device: string
  deviceType: string
  image?: string
  inventory?: number | string
  list?: string
  position?: number | string
  price: string
  productId: string
  productName: string
  quantity?: [] | number | string
  sku: string
  variantId: string
  variantName: string
}

export interface ElevarUser {
  cartTotal?: string
  consent?: boolean
  email?: string
  id?: string
  loggedIn?: boolean
  products?: Array<ElevarProductProps>
}

type DataLayerEvent = { event: string } & Record<string, any>

const formatElevarProduct = ({
  brand,
  compareAtPrice,
  device,
  deviceType,
  image,
  inventory,
  list,
  position,
  price,
  productId,
  productName,
  quantity,
  sku,
  variantId,
  variantName,
}: ElevarProductProps) => {
  const cleanedVariantId = variantId.replace(
    "gid://shopify/ProductVariant/",
    ""
  )
  const product: any = {
    brand,
    category: `${deviceType} / ${device}`,
    id: sku,
    name: productName,
    price,
    product_id: getDecodedShopifyProductIdString(productId),
    sku,
    variant_id: cleanedVariantId,
  }

  if (image) {
    product.image = image.replace("https:", "")
  }

  if (compareAtPrice) {
    product.compare_at_price = compareAtPrice
  }

  if (variantName) {
    product.variant = variantName
  }

  if (inventory !== undefined) {
    product.inventory =
      typeof inventory === "string" ? inventory : String(inventory)
  }

  if (quantity !== undefined) {
    product.quantity =
      typeof quantity === "string" ? quantity : String(quantity)
  }

  if (position) {
    product.position =
      typeof position === "string" ? parseInt(position) : position
  }

  if (list) {
    product.list = list
  }

  return product
}

const getLocation = () => {
  const _window = typeof window !== "undefined" && (window as any)
  return _window?.location?.pathname || ""
}

// Stable empty array reference for SSR
const EMPTY_DATALAYER_ELEVAR: any[] = []

// Always return the *live* array on window so queued events persist
const getDataLayer = () => {
  const _window = typeof window !== "undefined" && (window as any)
  if (!_window) {
    return EMPTY_DATALAYER_ELEVAR
  }
  _window.dataLayer = _window.dataLayer || []
  return _window.dataLayer
}

// Return a closure that keeps the correct receiver (the array) every time
const getPushToDataLayer = () => {
  return (payload: DataLayerEvent) => {
    const w = typeof window !== "undefined" ? (window as any) : null
    if (!w) return

    const dl: any[] = (w.ElevarDataLayer = w.ElevarDataLayer || [])

    // Guard: only push real event objects
    if (!payload || typeof payload !== "object" || !("event" in payload)) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "pushToDataLayer called without a valid event object:",
          payload
        )
      }
      return
    }

    dl.push(payload) // correct receiver
  }
}

const getPushContextToDataLayer = () => {
  const _window = typeof window !== "undefined" && (window as any)
  return _window?.ElevarInvalidateContext || function () {}
}

const ensureElevarLayers = () => {
  const w = typeof window !== "undefined" ? (window as any) : null
  if (!w) {
    return
  }
  w.ElevarDataLayer = w.ElevarDataLayer || []
  w.dataLayer = w.dataLayer || []
}

export default () => {
  ensureElevarLayers()
  const pushContextToDataLayer = React.useMemo(
    () => getPushContextToDataLayer(),
    []
  )
  const pushToDataLayer = React.useMemo(() => getPushToDataLayer(), [])
  const pushQueue = React.useRef<Array<any>>([])

  const processQueue = () => {
    try {
      const location = getLocation()
      const dataLayer = getDataLayer()
      const lastUserDataEvent = dataLayer
        .slice()
        .reverse()
        .find(
          ({ event }) => event && event === INTERNAL_USER_DATA_PUSHED_FOR_ELEVAR
        )
      const userDataPushed =
        lastUserDataEvent && lastUserDataEvent.location === location

      if (userDataPushed && pushQueue.current.length > 0) {
        while (pushQueue.current.length) {
          const event = pushQueue.current.shift()
          pushToDataLayer(event)
        }
      } else if (pushQueue.current.length > 0) {
        setTimeout(() => {
          processQueue()
        }, 2000)
      }
    } catch (error) {
      console.log("Elevar push error:", error)
    }
  }

  const handlePushToDataLayer = (data) => {
    pushQueue.current.push(data)
    processQueue()
  }

  const elevarOnUserData = React.useCallback(
    (user: ElevarUser) => {
      try {
        const location = getLocation()
        const dataLayer = getDataLayer()
        const products = user.products || []

        if (user.id) {
          pushContextToDataLayer()
          pushToDataLayer({
            cart_total: user.cartTotal,
            ecommerce: {
              cart_contents: {
                products: products.map(formatElevarProduct),
              },
              currencyCode: "USD",
            },
            event: ELEVAR_USER_DATA,
            user_properties: {
              customer_email: user.email,
              customer_id: user.id.replace("gid://shopify/Customer/", ""),
              user_consent: user.consent ? "yes" : "no_interaction",
              visitor_type: user.loggedIn ? "logged_in" : "guest",
            },
          })
          dataLayer.push({
            event: INTERNAL_USER_DATA_PUSHED_FOR_ELEVAR,
            location,
          })
        } else {
          pushContextToDataLayer()
          pushToDataLayer({
            cart_total: user.cartTotal,
            ecommerce: {
              cart_contents: {
                products: products.map(formatElevarProduct),
              },
              currencyCode: "USD",
            },
            event: ELEVAR_USER_DATA,
            user_properties: {
              visitor_type: "guest",
            },
          })
          dataLayer.push({
            event: INTERNAL_USER_DATA_PUSHED_FOR_ELEVAR,
            location,
          })
        }
      } catch (error) {
        console.log("Elevar init error:", error)
      }
    },
    [pushToDataLayer]
  )

  const elevarOnSignUp = React.useCallback(
    (user: ElevarUser) => {
      handlePushToDataLayer({
        event: ELEVAR_SIGN_UP,
        user_properties: {
          customer_email: user.email,
          customer_id: user.id,
          visitor_type: "logged_in",
        },
      })
    },
    [pushToDataLayer]
  )

  const elevarOnLogin = React.useCallback(
    (user: ElevarUser) => {
      handlePushToDataLayer({
        event: ELEVAR_LOGIN,
        user_properties: {
          customer_email: user.email,
          customer_id: user.id,
          visitor_type: "logged_in",
        },
      })
    },
    [pushToDataLayer]
  )

  // Required: position, list
  const elevarOnViewItemList = React.useCallback(
    (products: Array<ElevarProductProps>) => {
      const batches = _.chunk(products, 10)

      batches?.forEach((batch) => {
        handlePushToDataLayer({
          ecommerce: {
            currencyCode: "USD",
            impressions: batch.map(formatElevarProduct),
          },
          event: ELEVAR_VIEW_ITEM_LIST,
        })
      })
    },
    [pushToDataLayer]
  )

  // Required: position
  const elevarOnViewSearchResults = React.useCallback(
    (products: Array<ElevarProductProps>) => {
      handlePushToDataLayer({
        ecommerce: {
          actionField: { list: "search results" },
          currencyCode: "USD",
          impressions: products.map(formatElevarProduct),
        },
        event: ELEVAR_VIEW_SEARCH_RESULTS,
      })
    },
    [pushToDataLayer]
  )

  // Required: position, list
  // When a user clicks a product card, save ElevarProductProps in local storage
  const elevarOnSelectItem = React.useCallback(
    (product: ElevarProductProps, list: string) => {
      handlePushToDataLayer({
        ecommerce: {
          click: {
            actionField: { list: list },
            products: [formatElevarProduct(product)],
          },
          currencyCode: "USD",
        },
        event: ELEVAR_SELECT_ITEM,
      })
    },
    [pushToDataLayer]
  )

  // Required: variantName, image, inventory, list
  // Trigger every time a different variant is selected
  // Use list from ElevarProductProps in local storage if product ID matches
  const elevarOnViewItem = React.useCallback(
    (product: ElevarProductProps, list: string) => {
      handlePushToDataLayer({
        ecommerce: {
          currencyCode: "USD",
          detail: {
            actionField: { list: list },
            products: [formatElevarProduct(product)],
          },
        },
        event: ELEVAR_VIEW_ITEM,
      })
    },
    [pushToDataLayer]
  )

  // Required: variantName, image, quantity, list
  const elevarOnAddToCart = React.useCallback(
    (product: ElevarProductProps, list: string) => {
      handlePushToDataLayer({
        ecommerce: {
          add: {
            actionField: { list: list },
            products: [formatElevarProduct(product)],
          },
          currencyCode: "USD",
        },
        event: ELEVAR_ADD_TO_CART,
      })
    },
    [pushToDataLayer]
  )

  // Required: variantName, image, quantity, list
  const elevarOnRemoveFromCart = React.useCallback(
    (product: ElevarProductProps, list: string) => {
      handlePushToDataLayer({
        ecommerce: {
          currencyCode: "USD",
          remove: {
            actionField: { list: list },
            products: [formatElevarProduct(product)],
          },
        },
        event: ELEVAR_REMOVE_FROM_CART,
      })
    },
    [pushToDataLayer]
  )

  const elevarOnViewCart = React.useCallback(
    (products: Array<ElevarProductProps>, cartTotal: string) => {
      handlePushToDataLayer({
        cart_total: cartTotal,
        ecommerce: {
          currencyCode: "USD",
          impressions: products.map(formatElevarProduct),
        },
        event: ELEVAR_VIEW_CART,
      })
    },
    [pushToDataLayer]
  )

  return {
    elevarOnAddToCart,
    elevarOnLogin,
    elevarOnRemoveFromCart,
    elevarOnSelectItem,
    elevarOnSignUp,
    elevarOnUserData,
    elevarOnViewCart,
    elevarOnViewItem,
    elevarOnViewItemList,
    elevarOnViewSearchResults,
  }
}
